import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import WelcomePage from './pages/WelcomePage';
import AuthCallback from './pages/AuthCallback';
import LoginPage from './pages/LoginPage';
import StudentSignUp from './pages/StudentSignUp';
import ParentSignUp from './pages/ParentSignUp';
import TeacherSignUp from './pages/TeacherSignUp';
import StudentHome from './pages/StudentHome';
import DailyPractice from './pages/DailyPractice';
import DeepPractice from './pages/DeepPractice';
import SessionComplete from './pages/SessionComplete';
import ProgressPage from './pages/ProgressPage';
import AskZeno from './pages/AskZeno';
import StudentProfile from './pages/StudentProfile';
import ParentDashboard from './pages/ParentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminConfigCentre from './pages/AdminConfigCentre';
import QuestionManager from './pages/QuestionManager';
import UserManager from './pages/UserManager';
import OnboardingFlow from './pages/OnboardingFlow';
import ForgotPassword from './pages/ForgotPassword';
import LeaderboardPage from './pages/LeaderboardPage';
import TeacherPortal from './pages/TeacherPortal';
import StudentAssignments from './pages/StudentAssignments';
import WeaknessDetector from './pages/WeaknessDetector';
import WorksheetOfDay from './pages/WorksheetOfDay';
import CameraDoubt from './pages/CameraDoubt';
import ReferralPage from './pages/ReferralPage';
import OfflinePractice from './pages/OfflinePractice';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="text-center"><span className="text-5xl block animate-bounce">🦉</span><p className="font-bold mt-2" style={{ color: '#1B4F8A' }}>Loading...</p></div>
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicRoute><WelcomePage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/signup/student" element={<PublicRoute><StudentSignUp /></PublicRoute>} />
      <Route path="/signup/parent" element={<PublicRoute><ParentSignUp /></PublicRoute>} />
      <Route path="/signup/teacher" element={<PublicRoute><TeacherSignUp /></PublicRoute>} />
      {/* Student Routes */}
      <Route path="/home" element={<ProtectedRoute roles={['student']}><StudentHome /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute roles={['student']}><OnboardingFlow /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute roles={['student']}><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/practice" element={<ProtectedRoute roles={['student']}><DailyPractice /></ProtectedRoute>} />
      <Route path="/deep-practice" element={<ProtectedRoute roles={['student']}><DeepPractice /></ProtectedRoute>} />
      <Route path="/assignments" element={<ProtectedRoute roles={['student']}><StudentAssignments /></ProtectedRoute>} />
      <Route path="/weakness" element={<ProtectedRoute roles={['student']}><WeaknessDetector /></ProtectedRoute>} />
      <Route path="/worksheet-of-day" element={<ProtectedRoute roles={['student']}><WorksheetOfDay /></ProtectedRoute>} />
      <Route path="/camera-doubt" element={<ProtectedRoute roles={['student']}><CameraDoubt /></ProtectedRoute>} />
      <Route path="/offline-practice" element={<ProtectedRoute roles={['student']}><OfflinePractice /></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute roles={['student','teacher','parent']}><ReferralPage /></ProtectedRoute>} />
      <Route path="/session-complete" element={<ProtectedRoute roles={['student']}><SessionComplete /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute roles={['student']}><ProgressPage /></ProtectedRoute>} />
      <Route path="/zeno" element={<ProtectedRoute roles={['student']}><AskZeno /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute roles={['student']}><StudentProfile /></ProtectedRoute>} />
      {/* Parent Routes */}
      <Route path="/parent" element={<ProtectedRoute roles={['parent']}><ParentDashboard /></ProtectedRoute>} />
      {/* Teacher Routes */}
      <Route path="/teacher" element={<ProtectedRoute roles={['teacher']}><TeacherPortal /></ProtectedRoute>} />
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute roles={['admin']}><AdminConfigCentre /></ProtectedRoute>} />
      <Route path="/admin/questions" element={<ProtectedRoute roles={['admin']}><QuestionManager /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><UserManager /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <span className="text-5xl animate-bounce">🦉</span>
    </div>
  );
  if (user) {
    if (user.role === 'parent') return <Navigate to="/parent" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'teacher') return <Navigate to="/teacher" replace />;
    return <Navigate to="/home" replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
